import { JSX, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";

import AlertBanner from "lib/components/alert/AlertBanner";
import AlertMessage from "lib/components/alert/AlertMessage";
import InfoButton from "lib/components/button/InfoButton";
import InfoDrawer from "lib/components/drawer/InfoDrawer";

import { asTitle } from "utils/Utils";
import useGet from "utils/fetch/useGet";

import { usePrincipal } from "authentication/AuthProvider";

import OrganisationUserClient from "./OrganisationUserClient";
import OrganisationUsersTable from "./OrganisationUsersTable";

export type OrganisationUsersOverviewProps = {
	orgId: number;
};

export default function OrganisationUsersOverview(
	props: Readonly<OrganisationUsersOverviewProps>
): JSX.Element {
	const { t } = useTranslation();

	const { response } = useGet(OrganisationUserClient.getAll(props.orgId));

	const principal = usePrincipal();

	const [infoDialog, setInfoDialog] = useState(false);

	const openInfoDialog = useCallback(() => setInfoDialog(true), []);
	const closeInfoDialog = useCallback(() => setInfoDialog(false), []);

	return (
		<>
			<Grid container alignItems="center" columnGap={1}>
				<Grid item>
					<Typography variant="h1">
						{asTitle(t("user_other"))}
					</Typography>
				</Grid>
				<Grid item>
					<InfoButton onClick={openInfoDialog} />
				</Grid>
			</Grid>

			{response.state === "LOADING" && (
				<Box display="flex" justifyContent="center">
					<CircularProgress />
				</Box>
			)}

			{response.state === "ERROR" && (
				<AlertBanner
					alert={AlertMessage.error(
						t("loadObjectError", { object: "user_other" })
					)}
				/>
			)}

			{response.state === "SUCCESS" && (
				<OrganisationUsersTable
					users={response.data}
					currentUser={principal?.userId ?? 0}
					orgId={props.orgId}
				/>
			)}

			{infoDialog && (
				<InfoDrawer
					title={t("organisationUserInfoTitle")}
					message={t("organisationUsersInfoMessage")}
					onClose={closeInfoDialog}
				/>
			)}
		</>
	);
}
